import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useState } from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../../api/graphql'
import { OrganizationMembershipsQuery } from './OrganizationsSection'
import { Hugeicon } from '../../../../icons/Hugeicon'

type InviteMemberFormProps = {
  organizationId: string
  organizationName: string
  onCancel: () => void
  onSuccess: () => void
}

const INVITE_MEMBER_MUTATION = gql`
  mutation OrganizationMembershipInvite($input: OrganizationMembershipInviteInput!) {
    organizationMembershipInvite(input: $input) {
      organizationMembership {
        id
        invitedEmail
        invitedName
        roleTitle
        pending
      }
      errors {
        attribute
        message
      }
    }
  }
`

const InviteMemberForm: React.FC<InviteMemberFormProps> = ({ organizationId, organizationName, onCancel, onSuccess }) => {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')

  const [inviteMember, { loading }] = useMutation(INVITE_MEMBER_MUTATION)

  const handleSubmit = async () => {
    if (!email.trim()) {
      toastr.error('Email address is required')
      return
    }

    try {
      const result = await inviteMember({
        variables: {
          input: {
            organizationId,
            invitedEmail: email.trim(),
            invitedName: fullName.trim() || null,
          },
        },
        refetchQueries: [{ query: OrganizationMembershipsQuery }],
      })

      const response = result.data?.organizationMembershipInvite
      if (response?.errors?.length > 0) {
        toastr.error(response.errors[0].message)
        return
      }

      if (response?.organizationMembership) {
        toastr.success('Invitation sent!')
        setFullName('')
        setEmail('')
        onSuccess()
      }
    } catch (error) {
      toastr.error('Failed to send invitation')
    }
  }

  return (
    <StyledFormContainer>
      <StyledFormHeader>
        <StyledPaperPlaneIcon />
        <StyledFormHeaderText>
          <StyledFormTitle>Invite a New Member</StyledFormTitle>
          <StyledFormDescription>
            The new member will receive an email invitation. Once they accept, they'll be able to create and manage
            events on behalf of <strong>{organizationName}</strong>.
          </StyledFormDescription>
        </StyledFormHeaderText>
      </StyledFormHeader>

      <StyledFormFields>
        <StyledFieldGroup>
          <StyledLabel>Full Name</StyledLabel>
          <StyledInput
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            placeholder="e.g., Jane Smith"
          />
        </StyledFieldGroup>

        <StyledFieldGroup>
          <StyledLabel>Email Address</StyledLabel>
          <StyledInput
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="e.g., jane@example.com"
          />
        </StyledFieldGroup>

        <StyledFieldGroup>
          <StyledLabel>Role</StyledLabel>
          <StyledInput type="text" value="Member" disabled />
        </StyledFieldGroup>

        <StyledButtonRow>
          <StyledSendButton type="button" onClick={handleSubmit} disabled={loading}>
            <SendIcon />
            {loading ? 'Sending...' : 'Send Invitation'}
          </StyledSendButton>
          <StyledCancelButton type="button" onClick={onCancel}>
            Cancel
          </StyledCancelButton>
        </StyledButtonRow>
      </StyledFormFields>
    </StyledFormContainer>
  )
}

export default InviteMemberForm

const StyledPaperPlaneIcon: React.FC = () => <Hugeicon name="sent" size={24} />

const SendIcon: React.FC = () => <Hugeicon name="sent" size={16} />

const StyledFormContainer = styled.div`
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`

const StyledFormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  color: #a855f7;
`

const StyledFormHeaderText = styled.div`
  flex: 1;
`

const StyledFormTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 4px 0;
`

const StyledFormDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;

  strong {
    color: #a855f7;
  }
`

const StyledFormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const StyledLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #374151;
`

const StyledInput = styled.input`
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #102638;
  transition: border-color 0.15s ease-out;

  &:focus {
    outline: none;
    border-color: #a855f7;
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
    cursor: not-allowed;
  }
`

const StyledButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
`

const StyledSendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 1;
  background: #22c55e;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease-out;

  &:hover:not(:disabled) {
    background: #16a34a;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const StyledCancelButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease-out;

  &:hover {
    background: #e5e7eb;
  }
`
